import { useCallback } from 'react';
import { createObjectCsvStringifier } from 'csv-writer';

interface CSVHeader {
  id: string;
  title: string;
}

interface UseCSVExportOptions {
  headers: CSVHeader[];
  headerIdDelimiter?: string;
}

interface UseCSVExportResult {
  exportToCSV: (data: Record<string, any>[], filename: string) => void;
  stringifyCSV: (data: Record<string, any>[]) => string;
  downloadCSV: (csvContent: string, filename: string) => void;
}

export const useCSVExport = ({
  headers,
  headerIdDelimiter,
}: UseCSVExportOptions): UseCSVExportResult => {
  const stringifyCSV = useCallback(
    (data: Record<string, any>[]): string => {
      const stringifier = createObjectCsvStringifier({
        header: headers,
        headerIdDelimiter,
      });

      return stringifier.getHeaderString() + stringifier.stringifyRecords(data);
    },
    [headers, headerIdDelimiter],
  );

  const downloadCSV = useCallback((csvContent: string, filename: string) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);
  }, []);

  const exportToCSV = useCallback(
    (data: Record<string, any>[], filename: string) => {
      const csvContent = stringifyCSV(data);
      downloadCSV(csvContent, filename);
    },
    [stringifyCSV, downloadCSV],
  );

  return {
    exportToCSV,
    stringifyCSV,
    downloadCSV,
  };
};
